import React from 'react'
import { Box, Typography, Button } from '@mui/material'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Navebarehero } from '../Components/Navebarehero'
import { aboutUsData } from '../Data/aboutUsData'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut', delay },
  }),
}

// هيرو صفحة الأباوت - نفس هيكل هيرو البلوجز/المشاريع، بس بصورة ثابتة بدل الفيديو
const AboutHero = ({ isArabic, data }) => {
  const lang = isArabic ? 'ar' : 'en'

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: '65vh', md: '75vh' },
        bgcolor: '#2B2B2B',
        overflow: 'hidden',
      }}
    >
      <Box
        component={motion.img}
        src={data.image}
        alt=""
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, ease: 'easeOut' }}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: 'grayscale(0.3) brightness(0.55)',
          zIndex: 0,
        }}
      />

      {/* overlay أساسي لتحسين تباين النص */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          background:
            'linear-gradient(180deg, rgba(43,43,43,0.55) 0%, rgba(122,46,58,0.18) 50%, rgba(43,43,43,0.55) 100%)',
        }}
      />

      {/* ✨ gradient ممتد لأسفل الصورة عشان يمتزج مع خلفية الصفحة */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -1,
          left: 0,
          right: 0,
          height: { xs: '40%', md: '50%' },
          background:
            'linear-gradient(180deg, rgba(43,43,43,0) 0%, rgba(43,43,43,0.35) 35%, rgba(43,43,43,0.8) 70%, #2B2B2B 100%)',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      />

      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 1.5,
          px: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: isArabic ? "'IBM Plex Sans Arabic', sans-serif" : "'Manrope', sans-serif",
            color: '#D9A5A5',
            fontWeight: 700,
            letterSpacing: 2,
            fontSize: '0.85rem',
            textTransform: 'uppercase',
          }}
        >
          {data.eyebrow[lang]}
        </Typography>
        <Typography
          variant="h1"
          sx={{
            fontFamily: isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif",
            fontWeight: isArabic ? 700 : 600,
            fontSize: { xs: '2.3rem', md: '3.8rem' },
            color: '#fff',
          }}
        >
          {data.title[lang]}
        </Typography>
        <Typography sx={{ color: 'rgba(217,214,207,0.85)', maxWidth: 560 }}>
          {data.subtitle[lang]}
        </Typography>
      </Box>
    </Box>
  )
}

export const AboutUsPages = () => {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const isArabic = (i18n.language || 'ar').startsWith('ar')
  const lang = isArabic ? 'ar' : 'en'

  const headingFont = isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif"
  const bodyFont = isArabic ? "'IBM Plex Sans Arabic', sans-serif" : "'Manrope', sans-serif"

  const { hero, story, stats, valuesSection } = aboutUsData

  return (
    <Box sx={{ bgcolor: '#2B2B2B', minHeight: '100vh' }}>
      <Navebarehero />

      <AboutHero isArabic={isArabic} data={hero} />

      {/* سيكشن قصتنا - نص + صورة */}
      <Box sx={{ bgcolor: '#2B2B2B', py: { xs: 10, md: 14 } }}>
        <Box
          sx={{
            maxWidth: 1300,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 5, md: 8 },
            alignItems: 'center',
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0, x: isArabic ? 40 : -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            sx={{ order: { xs: 1, md: isArabic ? 2 : 1 } }}
          >
            <Typography
              sx={{
                fontFamily: bodyFont,
                color: '#7A2E3A',
                fontWeight: 700,
                letterSpacing: 2,
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                mb: 1.5,
              }}
            >
              {story.eyebrow[lang]}
            </Typography>
            <Typography
              sx={{
                fontFamily: headingFont,
                fontWeight: isArabic ? 700 : 500,
                fontSize: { xs: '2rem', md: '2.6rem' },
                color: '#D9D6CF',
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              {story.title[lang]}
            </Typography>

            {story.paragraphs.map((para, index) => (
              <Typography
                key={index}
                sx={{
                  fontFamily: bodyFont,
                  color: 'rgba(217,214,207,0.75)',
                  fontSize: '1rem',
                  lineHeight: 2,
                  mb: index === story.paragraphs.length - 1 ? 0 : 2,
                }}
              >
                {para[lang]}
              </Typography>
            ))}

            <Button
              onClick={() => navigate('/contact')}
              endIcon={<ArrowForwardIcon sx={{ transform: isArabic ? 'scaleX(-1)' : 'none' }} />}
              variant="contained"
              sx={{
                mt: 4,
                bgcolor: '#7A2E3A',
                color: '#D9D6CF',
                px: 4,
                py: 1.4,
                borderRadius: '999px',
                fontFamily: bodyFont,
                fontWeight: 600,
                '&:hover': { bgcolor: '#8f3646' },
              }}
            >
              {story.ctaLabel[lang]}
            </Button>
          </Box>

          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            sx={{
              order: { xs: 2, md: isArabic ? 1 : 2 },
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
              aspectRatio: '4 / 3',
            }}
          >
            <Box
              component="img"
              src={story.image}
              alt={story.title[lang]}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        </Box>
      </Box>

      {/* سيكشن الإحصائيات */}
      <Box sx={{ bgcolor: '#242424', py: { xs: 8, md: 10 } }}>
        <Box
          sx={{
            maxWidth: 1300,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: { xs: 4, md: 3 },
          }}
        >
          {stats.map((stat, index) => (
            <Box
              key={stat.value}
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              sx={{ textAlign: 'center' }}
            >
              <Typography
                sx={{
                  fontFamily: headingFont,
                  fontWeight: 700,
                  fontSize: { xs: '2.2rem', md: '2.8rem' },
                  color: '#7A2E3A',
                  lineHeight: 1,
                  mb: 1,
                }}
              >
                {stat.value}
              </Typography>
              <Typography sx={{ fontFamily: bodyFont, color: 'rgba(217,214,207,0.75)', fontSize: '0.9rem' }}>
                {stat.label[lang]}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* سيكشن القيم */}
      <Box sx={{ bgcolor: '#2B2B2B', py: { xs: 10, md: 14 } }}>
        <Box sx={{ maxWidth: 1300, mx: 'auto', px: { xs: 3, md: 6 } }}>
          <Box
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}
          >
            <Typography
              component={motion.p}
              custom={0}
              variants={fadeUp}
              sx={{
                fontFamily: bodyFont,
                color: '#7A2E3A',
                fontWeight: 700,
                letterSpacing: 2,
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                mb: 1.5,
              }}
            >
              {valuesSection.eyebrow[lang]}
            </Typography>
            <Typography
              component={motion.h2}
              custom={0.1}
              variants={fadeUp}
              sx={{
                fontFamily: headingFont,
                fontWeight: isArabic ? 700 : 500,
                fontSize: { xs: '2.2rem', md: '3rem' },
                color: '#D9D6CF',
              }}
            >
              {valuesSection.title[lang]}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 3,
            }}
          >
            {valuesSection.items.map((value, index) => {
              const Icon = value.icon
              return (
                <Box
                  key={value.title.en}
                  component={motion.div}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  sx={{
                    borderRadius: '18px',
                    border: '1px solid rgba(217,214,207,0.12)',
                    bgcolor: 'rgba(217,214,207,0.03)',
                    p: 3.5,
                    transition: 'border-color 0.3s ease',
                    '&:hover': { borderColor: 'rgba(122,46,58,0.5)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '14px',
                      bgcolor: 'rgba(122,46,58,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                    }}
                  >
                    <Icon sx={{ color: '#7A2E3A', fontSize: 26 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: headingFont,
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      color: '#D9D6CF',
                      mb: 1.2,
                    }}
                  >
                    {value.title[lang]}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: bodyFont,
                      color: 'rgba(217,214,207,0.65)',
                      fontSize: '0.9rem',
                      lineHeight: 1.8,
                    }}
                  >
                    {value.desc[lang]}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}